import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Sun, Eye, EyeOff, ArrowRight, Leaf } from "lucide-react";
import { toast } from "sonner";

const FEATURES = [
  "8-phase project templates with 60+ pre-built tasks",
  "GST-compliant PO & PI document generator",
  "Real-time client dashboards & notifications",
  "Role-based access for your entire team",
];

const inputStyle = {
  width: "100%",
  background: "#FFFFFF",
  border: "1px solid rgba(46,100,68,0.15)",
  borderRadius: "10px",
  color: "#1B2B22",
  fontSize: "14px",
  fontFamily: "Manrope, sans-serif",
  padding: "11px 14px",
  outline: "none",
  transition: "border-color 0.2s ease, box-shadow 0.2s ease",
};

const labelStyle = {
  display: "block",
  fontSize: "11px",
  fontWeight: "600",
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "#5A7264",
  marginBottom: "6px",
  fontFamily: "Manrope, sans-serif",
};

export default function AuthPage({ mode = "login" }) {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/app/dashboard";

  const params = new URLSearchParams(location.search);
  const inviteEmail = params.get("email") || "";

  const [activeMode, setActiveMode] = useState(mode);
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: inviteEmail, password: "" });
  const [regForm, setRegForm] = useState({
    company_name: "", email: "", password: "", full_name: "", phone: "", gstin: ""
  });

  const isInviteFlow = !!inviteEmail && activeMode === "login";

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(loginForm.email, loginForm.password);
      toast.success("Welcome back!");
      if (data.user.role === "super_admin")  navigate("/superadmin");
      else if (data.user.role === "client")  navigate("/app/projects");
      else                                   navigate(from);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed. Check your credentials.");
    } finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!regForm.company_name || !regForm.email || !regForm.password || !regForm.full_name) {
      toast.error("Please fill all required fields"); return;
    }
    if (regForm.phone && regForm.phone.trim()) {
      const digits = regForm.phone.replace(/[^0-9]/g, "");
      if (!/^[0-9+\-\s()]+$/.test(regForm.phone) || digits.length < 7 || digits.length > 15) {
        toast.error("Please enter a valid phone number (7–15 digits)."); return;
      }
    }
    setLoading(true);
    try {
      await register(regForm);
      toast.success("Account created! Welcome to SunBiz.");
      navigate("/onboarding");
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      if (detail.toLowerCase().includes("already registered")) {
        toast.error("This email is already registered — you may have been invited. Switching to Sign In.");
        setLoginForm(p => ({ ...p, email: regForm.email }));
        setActiveMode("login");
      } else {
        toast.error(detail || "Registration failed. Try again.");
      }
    } finally { setLoading(false); }
  };

  return (
    <div
      className="min-h-screen flex"
      style={{ background: "#F5F8F5", fontFamily: "Manrope, sans-serif" }}
    >
      {/* Left: Hero Panel */}
      <div
        className="hidden lg:flex flex-col justify-between w-1/2 relative overflow-hidden"
        style={{ padding: "48px" }}
      >
        {/* Soft green gradient background */}
        <div
          className="absolute inset-0"
          style={{
            background: `
              radial-gradient(ellipse at 25% 35%, rgba(47,125,82,0.12) 0%, transparent 55%),
              radial-gradient(ellipse at 75% 70%, rgba(255,183,3,0.1) 0%, transparent 55%),
              radial-gradient(ellipse at 50% 90%, rgba(47,125,82,0.06) 0%, transparent 50%),
              #EFF6F1
            `,
          }}
        />
        {/* Solar panel background overlay */}
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1509391366360-2e959784a276?crop=entropy&cs=srgb&fm=jpg&q=75&w=800')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        {/* Subtle grid */}
        <div
          className="absolute inset-0 opacity-[0.025]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(46,100,68,1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(46,100,68,1) 1px, transparent 1px)
            `,
            backgroundSize: "40px 40px",
          }}
        />

        <div className="relative z-10">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-16">
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{ background: "#FFB703", boxShadow: "0 0 18px rgba(255,183,3,0.35)" }}
            >
              <Sun size={17} style={{ color: "#1B2B22" }} />
            </div>
            <div>
              <div className="font-heading font-bold text-lg leading-none tracking-tight" style={{ color: "#1B2B22" }}>SunBiz</div>
              <div className="text-[9px] tracking-[0.2em] uppercase mt-0.5" style={{ color: "#8AA496" }}>by Sun Bizcon</div>
            </div>
          </div>

          {/* Badge */}
          <div className="mb-4">
            <span
              className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full mb-6"
              style={{
                background: "rgba(47,125,82,0.1)",
                border: "1px solid rgba(47,125,82,0.2)",
                color: "#2F7D52",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
              }}
            >
              <Leaf size={11} />
              Built for Solar EPC India
            </span>
          </div>

          <h1
            className="font-heading font-bold leading-tight mb-4"
            style={{ fontSize: "40px", letterSpacing: "-0.03em", color: "#1B2B22" }}
          >
            From Survey to{" "}
            <span style={{ color: "#2F7D52" }}>Switch-On</span>,
            <br />
            Every Project On Track.
          </h1>
          <p style={{ color: "#5A7264", fontSize: "15px", lineHeight: "1.7", maxWidth: "380px" }}>
            Replace Excel sheets and WhatsApp follow-ups with the only platform built for Solar EPC companies in India.
          </p>
        </div>

        {/* Features list */}
        <div className="relative z-10 space-y-3">
          {FEATURES.map((item, i) => (
            <div key={i} className="flex items-center gap-3">
              <div
                className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: "rgba(47,125,82,0.12)", border: "1px solid rgba(47,125,82,0.2)" }}
              >
                <div className="w-1.5 h-1.5 rounded-full" style={{ background: "#2F7D52" }} />
              </div>
              <span style={{ color: "#5A7264", fontSize: "13px" }}>{item}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Right: Form Panel */}
      <div
        className="flex-1 flex items-center justify-center"
        style={{ padding: "32px", background: "#FFFFFF", borderLeft: "1px solid rgba(46,100,68,0.08)" }}
      >
        <div style={{ width: "100%", maxWidth: "420px" }}>
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-2.5 mb-10">
            <div
              className="w-8 h-8 rounded-xl flex items-center justify-center"
              style={{ background: "#FFB703", boxShadow: "0 0 16px rgba(255,183,3,0.35)" }}
            >
              <Sun size={15} style={{ color: "#1B2B22" }} />
            </div>
            <span className="font-heading font-bold text-lg" style={{ color: "#1B2B22" }}>SunBiz</span>
          </div>

          <div className="mb-8">
            <h2 className="font-heading font-bold text-2xl mb-1" style={{ letterSpacing: "-0.03em", color: "#1B2B22" }}>
              {activeMode === "login" ? "Welcome back" : "Start your free trial"}
            </h2>
            <p style={{ color: "#8AA496", fontSize: "14px" }}>
              {activeMode === "login"
                ? "Sign in to your workspace"
                : "14 days free. No credit card required."}
            </p>
          </div>

          {/* Tab switcher */}
          <div
            className="flex mb-8 p-1 rounded-xl"
            style={{ background: "#F5F8F5", border: "1px solid rgba(46,100,68,0.1)" }}
          >
            {[["login", "Sign In"], ["register", "Free Trial"]].map(([m, label]) => (
              <button
                key={m}
                onClick={() => setActiveMode(m)}
                data-testid={`${m === "login" ? "login" : "register"}-tab`}
                style={{
                  flex: 1,
                  padding: "9px 0",
                  borderRadius: "9px",
                  fontSize: "13px",
                  fontWeight: "600",
                  border: "none",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                  fontFamily: "Manrope, sans-serif",
                  background: activeMode === m ? "#FFFFFF" : "transparent",
                  color: activeMode === m ? "#2F7D52" : "#8AA496",
                  boxShadow: activeMode === m ? "0 1px 6px rgba(46,100,68,0.12)" : "none",
                }}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Login Form */}
          {activeMode === "login" ? (
            <form onSubmit={handleLogin} className="space-y-5" data-testid="login-form">
              {/* Invite helper banner */}
              {isInviteFlow && (
                <div style={{ background: "rgba(255,183,3,0.08)", border: "1px solid rgba(255,183,3,0.25)", borderRadius: "8px", padding: "10px 14px", fontSize: "12px", color: "#92400E", lineHeight: 1.5 }}>
                  You've been invited! Your email is pre-filled. Enter the <strong>temporary password</strong> from your invite email below.
                </div>
              )}
              <div>
                <label style={labelStyle}>Email Address</label>
                <input
                  type="email" required autoComplete="email"
                  value={loginForm.email}
                  onChange={e => setLoginForm(p => ({ ...p, email: e.target.value }))}
                  placeholder="you@company.com"
                  style={inputStyle}
                  onFocus={e => { e.target.style.borderColor = "rgba(47,125,82,0.5)"; e.target.style.boxShadow = "0 0 0 3px rgba(47,125,82,0.1)"; }}
                  onBlur={e  => { e.target.style.borderColor = "rgba(46,100,68,0.15)"; e.target.style.boxShadow = "none"; }}
                  data-testid="login-email"
                />
              </div>
              <div>
                <label style={labelStyle}>Password</label>
                <div className="relative">
                  <input
                    type={showPass ? "text" : "password"} required autoComplete="current-password"
                    value={loginForm.password}
                    onChange={e => setLoginForm(p => ({ ...p, password: e.target.value }))}
                    placeholder="••••••••"
                    style={{ ...inputStyle, paddingRight: "44px" }}
                    onFocus={e => { e.target.style.borderColor = "rgba(47,125,82,0.5)"; e.target.style.boxShadow = "0 0 0 3px rgba(47,125,82,0.1)"; }}
                    onBlur={e  => { e.target.style.borderColor = "rgba(46,100,68,0.15)"; e.target.style.boxShadow = "none"; }}
                    data-testid="login-password"
                  />
                  <button
                    type="button" onClick={() => setShowPass(!showPass)}
                    style={{ position: "absolute", right: "14px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#8AA496" }}
                  >
                    {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="login-submit-btn"
                style={{
                  width: "100%",
                  padding: "13px 0",
                  borderRadius: "10px",
                  fontWeight: "700",
                  fontSize: "14px",
                  fontFamily: "Manrope, sans-serif",
                  border: "none",
                  cursor: loading ? "not-allowed" : "pointer",
                  opacity: loading ? 0.6 : 1,
                  background: "#2F7D52",
                  color: "white",
                  boxShadow: "0 4px 14px rgba(47,125,82,0.3)",
                  transition: "box-shadow 0.2s ease, transform 0.12s ease, background 0.2s ease",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                }}
                onMouseEnter={e => { if (!loading) { e.currentTarget.style.background = "#266B44"; e.currentTarget.style.boxShadow = "0 6px 20px rgba(47,125,82,0.4)"; e.currentTarget.style.transform = "translateY(-1px)"; }}}
                onMouseLeave={e => { e.currentTarget.style.background = "#2F7D52"; e.currentTarget.style.boxShadow = "0 4px 14px rgba(47,125,82,0.3)"; e.currentTarget.style.transform = "translateY(0)"; }}
              >
                {loading ? "Signing in..." : <><span>Sign In</span><ArrowRight size={16} /></>}
              </button>
              <p style={{ textAlign: "center", fontSize: "12px", color: "#8AA496" }}>
                Super Admin? Use your admin credentials.
              </p>
            </form>
          ) : (
            <form onSubmit={handleRegister} className="space-y-4" data-testid="register-form">
              <div>
                <label style={labelStyle}>Company Name *</label>
                <input type="text" required value={regForm.company_name} onChange={e => setRegForm(p => ({ ...p, company_name: e.target.value }))} placeholder="Acme Solar EPC Pvt Ltd" style={inputStyle} onFocus={e => { e.target.style.borderColor = "rgba(47,125,82,0.5)"; e.target.style.boxShadow = "0 0 0 3px rgba(47,125,82,0.1)"; }} onBlur={e => { e.target.style.borderColor = "rgba(46,100,68,0.15)"; e.target.style.boxShadow = "none"; }} data-testid="reg-company" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label style={labelStyle}>Full Name *</label>
                  <input type="text" required value={regForm.full_name} onChange={e => setRegForm(p => ({ ...p, full_name: e.target.value }))} placeholder="Rajesh Kumar" style={inputStyle} onFocus={e => { e.target.style.borderColor = "rgba(47,125,82,0.5)"; e.target.style.boxShadow = "0 0 0 3px rgba(47,125,82,0.1)"; }} onBlur={e => { e.target.style.borderColor = "rgba(46,100,68,0.15)"; e.target.style.boxShadow = "none"; }} data-testid="reg-name" />
                </div>
                <div>
                  <label style={labelStyle}>Phone</label>
                  <input type="tel" value={regForm.phone} onChange={e => setRegForm(p => ({ ...p, phone: e.target.value }))} placeholder="+91 98765 43210" style={inputStyle} onFocus={e => { e.target.style.borderColor = "rgba(47,125,82,0.5)"; e.target.style.boxShadow = "0 0 0 3px rgba(47,125,82,0.1)"; }} onBlur={e => { e.target.style.borderColor = "rgba(46,100,68,0.15)"; e.target.style.boxShadow = "none"; }} />
                </div>
              </div>
              <div>
                <label style={labelStyle}>Email Address *</label>
                <input type="email" required value={regForm.email} onChange={e => setRegForm(p => ({ ...p, email: e.target.value }))} placeholder="you@company.com" style={inputStyle} onFocus={e => { e.target.style.borderColor = "rgba(47,125,82,0.5)"; e.target.style.boxShadow = "0 0 0 3px rgba(47,125,82,0.1)"; }} onBlur={e => { e.target.style.borderColor = "rgba(46,100,68,0.15)"; e.target.style.boxShadow = "none"; }} data-testid="reg-email" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label style={labelStyle}>Password *</label>
                  <div className="relative">
                    <input type={showPass ? "text" : "password"} required minLength={6} value={regForm.password} onChange={e => setRegForm(p => ({ ...p, password: e.target.value }))} placeholder="Min 6 chars" style={{ ...inputStyle, paddingRight: "44px" }} onFocus={e => { e.target.style.borderColor = "rgba(47,125,82,0.5)"; e.target.style.boxShadow = "0 0 0 3px rgba(47,125,82,0.1)"; }} onBlur={e => { e.target.style.borderColor = "rgba(46,100,68,0.15)"; e.target.style.boxShadow = "none"; }} data-testid="reg-password" />
                    <button type="button" onClick={() => setShowPass(!showPass)} style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#8AA496" }}>
                      {showPass ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>GSTIN</label>
                  <input type="text" value={regForm.gstin} onChange={e => setRegForm(p => ({ ...p, gstin: e.target.value }))} placeholder="22AAAAA0000A1Z5" style={inputStyle} onFocus={e => { e.target.style.borderColor = "rgba(47,125,82,0.5)"; e.target.style.boxShadow = "0 0 0 3px rgba(47,125,82,0.1)"; }} onBlur={e => { e.target.style.borderColor = "rgba(46,100,68,0.15)"; e.target.style.boxShadow = "none"; }} />
                </div>
              </div>
              <button
                type="submit" disabled={loading} data-testid="register-submit-btn"
                style={{
                  width: "100%", padding: "13px 0", borderRadius: "10px", fontWeight: "700", fontSize: "14px", fontFamily: "Manrope, sans-serif", border: "none", cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.6 : 1,
                  background: "#2F7D52", color: "white",
                  boxShadow: "0 4px 14px rgba(47,125,82,0.3)", transition: "box-shadow 0.2s ease, transform 0.12s ease, background 0.2s ease",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                }}
                onMouseEnter={e => { if (!loading) { e.currentTarget.style.background = "#266B44"; e.currentTarget.style.boxShadow = "0 6px 20px rgba(47,125,82,0.4)"; e.currentTarget.style.transform = "translateY(-1px)"; }}}
                onMouseLeave={e => { e.currentTarget.style.background = "#2F7D52"; e.currentTarget.style.boxShadow = "0 4px 14px rgba(47,125,82,0.3)"; e.currentTarget.style.transform = "translateY(0)"; }}
              >
                {loading ? "Creating account..." : <><span>Start Free Trial</span><ArrowRight size={16} /></>}
              </button>
              <p style={{ textAlign: "center", fontSize: "12px", color: "#8AA496" }}>
                14 days free trial · No credit card required · Cancel anytime
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
